import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { sanitizeHtml } from '../utils/sanitize';
import PageHeader from '../components/PageHeader';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { ScrollArea } from '../components/ui/scroll-area';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  GraduationCap,
  Video,
  FileText,
  Link as LinkIcon,
  Plus,
  Edit,
  Trash2,
  Upload,
  FolderPlus,
  Play,
  ExternalLink,
  ChevronRight,
  ChevronDown,
  Loader2,
  X,
  Save,
  Download,
  Eye,
  Maximize2,
  Minimize2
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TrainingPage = () => {
  const { isSecurityHead, hasPermission } = useAuth();
  
  // Permission checks
  const canUpload = hasPermission('can_upload_training');
  const canDelete = hasPermission('can_delete_training');
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [loading, setLoading] = useState(true);
  
  // Dialogs
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [itemDialogOpen, setItemDialogOpen] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  // Document Viewer state
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerFullscreen, setViewerFullscreen] = useState(false);
  const [viewerItem, setViewerItem] = useState(null);
  
  // Form states
  const [newCategory, setNewCategory] = useState({ name: '', description: '', parent_id: null });
  const [newItem, setNewItem] = useState({ title: '', description: '', category_id: '', item_type: 'document', url: '', content: '' });
  const [uploadData, setUploadData] = useState({ file: null, title: '', description: '', category_id: '' });
  const [newLink, setNewLink] = useState({ title: '', description: '', category_id: '', url: '' });
  
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchCategories();
    fetchItems();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API_URL}/training/categories`);
      setCategories(response.data);
      // Expand all by default
      const expanded = {};
      response.data.forEach(cat => { expanded[cat.id] = true; });
      setExpandedCategories(expanded);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const fetchItems = async () => {
    try {
      const response = await axios.get(`${API_URL}/training/items`);
      setItems(response.data);
    } catch (error) {
      console.error('Failed to fetch items:', error);
    } finally {
      setLoading(false);
    }
  };

  const createCategory = async () => {
    if (!newCategory.name) {
      toast.error('Please enter category name');
      return;
    }

    try {
      const response = await axios.post(`${API_URL}/training/categories`, newCategory);
      setCategories([...categories, response.data]);
      setCategoryDialogOpen(false);
      setNewCategory({ name: '', description: '', parent_id: null });
      toast.success('Category created!');
    } catch (error) {
      toast.error('Failed to create category');
    }
  };

  const deleteCategory = async (catId) => {
    if (!window.confirm('Delete this category and all its content?')) return;
    
    try {
      await axios.delete(`${API_URL}/training/categories/${catId}`);
      setCategories(categories.filter(c => c.id !== catId && c.parent_id !== catId));
      setItems(items.filter(i => i.category_id !== catId));
      toast.success('Category deleted');
    } catch (error) {
      toast.error('Failed to delete category');
    }
  };

  const uploadFile = async () => {
    if (!uploadData.file || !uploadData.category_id) {
      toast.error('Please select a file and category');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadData.file);
      formData.append('category_id', uploadData.category_id);
      if (uploadData.title) formData.append('title', uploadData.title);
      if (uploadData.description) formData.append('description', uploadData.description);

      const response = await axios.post(`${API_URL}/training/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setItems([response.data, ...items]);
      setUploadDialogOpen(false);
      setUploadData({ file: null, title: '', description: '', category_id: '' });
      if (fileInputRef.current) fileInputRef.current.value = '';
      toast.success('File uploaded!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload');
    } finally {
      setUploading(false);
    }
  };

  const createLink = async () => {
    if (!newLink.title || !newLink.url || !newLink.category_id) {
      toast.error('Please fill all required fields');
      return;
    }

    try {
      const response = await axios.post(`${API_URL}/training/items`, {
        ...newLink,
        item_type: 'link'
      });
      setItems([response.data, ...items]);
      setLinkDialogOpen(false);
      setNewLink({ title: '', description: '', category_id: '', url: '' });
      toast.success('Link added!');
    } catch (error) {
      toast.error('Failed to add link');
    }
  };

  const deleteItem = async (itemId) => {
    if (!window.confirm('Delete this training item?')) return;
    
    try {
      await axios.delete(`${API_URL}/training/items/${itemId}`);
      setItems(items.filter(i => i.id !== itemId));
      if (selectedItem?.id === itemId) setSelectedItem(null);
      toast.success('Item deleted');
    } catch (error) {
      toast.error('Failed to delete item');
    }
  };

  // Download training item
  const downloadItem = (item) => {
    if (!item) return;
    
    // If there's a file URL, download directly
    if (item.file_url) {
      const a = document.createElement('a');
      a.href = item.file_url;
      a.download = item.file_name || item.title;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      toast.success('Downloading...');
      return;
    }
    
    // If there's content, download as text file
    if (item.content) {
      const content = item.content;
      const filename = `${item.title.replace(/[^a-z0-9]/gi, '_')}.${item.content_type === 'html' ? 'html' : 'txt'}`;
      const mimeType = item.content_type === 'html' ? 'text/html' : 'text/plain';
      
      const blob = new Blob([content], { type: mimeType });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Document downloaded!');
      return;
    }
    
    // If there's a link URL
    if (item.url && item.item_type === 'link') {
      window.open(item.url, '_blank');
      return;
    }
    
    toast.error('No downloadable content');
  };

  // Open item in viewer
  const openInViewer = (item) => {
    setViewerItem(item);
    setViewerOpen(true);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploadData({ 
        ...uploadData, 
        file, 
        title: file.name.split('.')[0].replace(/[-_]/g, ' ') 
      });
    }
  };

  const toggleCategory = (catId) => {
    setExpandedCategories(prev => ({ ...prev, [catId]: !prev[catId] }));
  };

  // Get parent categories (no parent_id)
  const parentCategories = categories.filter(c => !c.parent_id);
  
  // Get subcategories for a parent
  const getSubcategories = (parentId) => categories.filter(c => c.parent_id === parentId);
  
  // Get items for a category
  const getCategoryItems = (catId) => items.filter(i => i.category_id === catId);

  const getItemIcon = (type) => {
    switch (type) {
      case 'video': return Video;
      case 'document': return FileText;
      case 'link': return LinkIcon;
      default: return FileText;
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="p-4 md:p-6 space-y-6" data-testid="training-page">
      {/* Header with Logo */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <PageHeader 
          title="Training" 
          subtitle="Videos, documents & resources"
        />
        
        {canUpload && (
          <div className="flex gap-2 flex-wrap">
            {/* Add Category */}
            <Dialog open={categoryDialogOpen} onOpenChange={setCategoryDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="border-white/20 hover:bg-white/5 rounded-sm font-mono text-xs uppercase">
                  <FolderPlus className="w-4 h-4 mr-2" />
                  Category
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-panel border-white/10 rounded-sm">
                <DialogHeader>
                  <DialogTitle className="font-chivo font-bold text-xl text-white">Add Category</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 pt-4">
                  <div className="space-y-2">
                    <Label className="font-mono text-xs uppercase text-muted-foreground">Name *</Label>
                    <Input
                      placeholder="Category name"
                      value={newCategory.name}
                      onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
                      className="bg-black border-white/20 rounded-sm"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="font-mono text-xs uppercase text-muted-foreground">Description</Label>
                    <Input
                      placeholder="Brief description"
                      value={newCategory.description}
                      onChange={(e) => setNewCategory({ ...newCategory, description: e.target.value })}
                      className="bg-black border-white/20 rounded-sm"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="font-mono text-xs uppercase text-muted-foreground">Parent Category (for subcategory)</Label>
                    <Select value={newCategory.parent_id || 'none'} onValueChange={(v) => setNewCategory({ ...newCategory, parent_id: v === 'none' ? null : v })}>
                      <SelectTrigger className="bg-black border-white/20 rounded-sm">
                        <SelectValue placeholder="None (top-level)" />
                      </SelectTrigger>
                      <SelectContent className="bg-panel border-white/10 rounded-sm">
                        <SelectItem value="none">None (top-level)</SelectItem>
                        {parentCategories.map(cat => (
                          <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button onClick={createCategory} className="w-full bg-primary hover:bg-primary/90 rounded-sm font-bold uppercase">
                    Create Category
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            {/* Upload File */}
            <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="border-white/20 hover:bg-white/5 rounded-sm font-mono text-xs uppercase">
                  <Upload className="w-4 h-4 mr-2" />
                  Upload
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-panel border-white/10 rounded-sm max-w-lg">
                <DialogHeader>
                  <DialogTitle className="font-chivo font-bold text-xl text-white">Upload Training Material</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 pt-4">
                  <div 
                    className="border-2 border-dashed border-white/20 rounded-sm p-6 text-center cursor-pointer hover:border-primary/50"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".mp4,.webm,.mov,.avi,.mkv,.pdf,.docx,.doc,.txt,.md"
                      onChange={handleFileSelect}
                      className="hidden"
                    />
                    <Upload className="w-10 h-10 text-muted-foreground mx-auto mb-2" />
                    {uploadData.file ? (
                      <div>
                        <p className="font-inter text-sm text-white">{uploadData.file.name}</p>
                        <p className="font-mono text-xs text-muted-foreground">{formatFileSize(uploadData.file.size)}</p>
                      </div>
                    ) : (
                      <div>
                        <p className="font-inter text-sm text-muted-foreground">Videos: MP4, WebM, MOV</p>
                        <p className="font-inter text-sm text-muted-foreground">Documents: PDF, DOCX, TXT</p>
                      </div>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label className="font-mono text-xs uppercase text-muted-foreground">Title</Label>
                    <Input
                      value={uploadData.title}
                      onChange={(e) => setUploadData({ ...uploadData, title: e.target.value })}
                      className="bg-black border-white/20 rounded-sm"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="font-mono text-xs uppercase text-muted-foreground">Description</Label>
                    <Textarea
                      value={uploadData.description}
                      onChange={(e) => setUploadData({ ...uploadData, description: e.target.value })}
                      className="bg-black border-white/20 rounded-sm min-h-[80px]"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="font-mono text-xs uppercase text-muted-foreground">Category *</Label>
                    <Select value={uploadData.category_id} onValueChange={(v) => setUploadData({ ...uploadData, category_id: v })}>
                      <SelectTrigger className="bg-black border-white/20 rounded-sm">
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent className="bg-panel border-white/10 rounded-sm">
                        {categories.map(cat => (
                          <SelectItem key={cat.id} value={cat.id}>
                            {cat.parent_id ? '└ ' : ''}{cat.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button onClick={uploadFile} disabled={uploading || !uploadData.file} className="w-full bg-primary hover:bg-primary/90 rounded-sm font-bold uppercase">
                    {uploading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Uploading...</> : 'Upload'}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            {/* Add Link */}
            <Dialog open={linkDialogOpen} onOpenChange={setLinkDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-white text-black hover:bg-gray-200 rounded-sm font-bold uppercase">
                  <LinkIcon className="w-4 h-4 mr-2" />
                  Add Link
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-panel border-white/10 rounded-sm">
                <DialogHeader>
                  <DialogTitle className="font-chivo font-bold text-xl text-white">Add External Link</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 pt-4">
                  <div className="space-y-2">
                    <Label className="font-mono text-xs uppercase text-muted-foreground">Title *</Label>
                    <Input
                      placeholder="Link title"
                      value={newLink.title}
                      onChange={(e) => setNewLink({ ...newLink, title: e.target.value })}
                      className="bg-black border-white/20 rounded-sm"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="font-mono text-xs uppercase text-muted-foreground">URL *</Label>
                    <Input
                      placeholder="https://..."
                      value={newLink.url}
                      onChange={(e) => setNewLink({ ...newLink, url: e.target.value })}
                      className="bg-black border-white/20 rounded-sm font-mono"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="font-mono text-xs uppercase text-muted-foreground">Description</Label>
                    <Textarea
                      value={newLink.description}
                      onChange={(e) => setNewLink({ ...newLink, description: e.target.value })}
                      className="bg-black border-white/20 rounded-sm min-h-[80px]"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="font-mono text-xs uppercase text-muted-foreground">Category *</Label>
                    <Select value={newLink.category_id} onValueChange={(v) => setNewLink({ ...newLink, category_id: v })}>
                      <SelectTrigger className="bg-black border-white/20 rounded-sm">
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent className="bg-panel border-white/10 rounded-sm">
                        {categories.map(cat => (
                          <SelectItem key={cat.id} value={cat.id}>
                            {cat.parent_id ? '└ ' : ''}{cat.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button onClick={createLink} className="w-full bg-primary hover:bg-primary/90 rounded-sm font-bold uppercase">
                    Add Link
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        )}
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Categories & Items List */}
        <Card className="bg-panel border-white/10 rounded-sm lg:col-span-1">
          <CardHeader className="border-b border-white/10 pb-4">
            <CardTitle className="font-mono text-xs uppercase tracking-wide text-muted-foreground">
              Categories & Content
            </CardTitle>
          </CardHeader>
          <ScrollArea className="h-[600px]">
            <CardContent className="p-2">
              {parentCategories.length === 0 ? (
                <div className="text-center py-8">
                  <GraduationCap className="w-12 h-12 text-muted-foreground mx-auto mb-3 opacity-50" />
                  <p className="font-mono text-sm text-muted-foreground">No categories yet</p>
                  {isSecurityHead && (
                    <p className="font-mono text-xs text-muted-foreground mt-1">Click &quot;Category&quot; to create one</p>
                  )}
                </div>
              ) : (
                <div className="space-y-1">
                  {parentCategories.map(category => (
                    <CategoryItem
                      key={category.id}
                      category={category}
                      subcategories={getSubcategories(category.id)}
                      items={getCategoryItems(category.id)}
                      allItems={items}
                      expanded={expandedCategories[category.id]}
                      onToggle={() => toggleCategory(category.id)}
                      onSelectItem={setSelectedItem}
                      selectedItem={selectedItem}
                      onDelete={isSecurityHead ? deleteCategory : null}
                      onDeleteItem={isSecurityHead ? deleteItem : null}
                      onViewItem={openInViewer}
                      onDownloadItem={downloadItem}
                      getItemIcon={getItemIcon}
                    />
                  ))}
                </div>
              )}
            </CardContent>
          </ScrollArea>
        </Card>

        {/* Content Viewer */}
        <Card className="bg-panel border-white/10 rounded-sm lg:col-span-2">
          {selectedItem ? (
            <>
              <CardHeader className="border-b border-white/10 pb-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {React.createElement(getItemIcon(selectedItem.item_type), { className: 'w-5 h-5 text-primary' })}
                    <div>
                      <CardTitle className="font-chivo font-bold text-xl text-white">
                        {selectedItem.title}
                      </CardTitle>
                      <p className="font-mono text-xs text-muted-foreground mt-1">
                        {selectedItem.item_type.toUpperCase()}
                        {selectedItem.file_name && ` • ${selectedItem.file_name}`}
                        {selectedItem.file_size && ` • ${formatFileSize(selectedItem.file_size)}`}
                      </p>
                    </div>
                  </div>
                  {isSecurityHead && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => deleteItem(selectedItem.id)}
                      className="border-alert-red/50 text-alert-red hover:bg-alert-red/10 rounded-sm"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
                {selectedItem.description && (
                  <p className="font-inter text-sm text-muted-foreground mt-3">
                    {selectedItem.description}
                  </p>
                )}
              </CardHeader>
              <CardContent className="p-6">
                {/* Video Player */}
                {selectedItem.item_type === 'video' && selectedItem.url && (
                  <div className="aspect-video bg-black rounded-sm overflow-hidden">
                    <video 
                      controls 
                      className="w-full h-full"
                      src={selectedItem.url}
                    >
                      Your browser does not support video playback.
                    </video>
                  </div>
                )}

                {/* Document Content */}
                {selectedItem.item_type === 'document' && selectedItem.content && (
                  <ScrollArea className="h-[500px]">
                    <div className="prose prose-invert prose-sm max-w-none">
                      {selectedItem.content_type === 'html' ? (
                        <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(`
                          <style>
                            .doc-content h1, .doc-content h2, .doc-content h3 { color: white; font-weight: 700; }
                            .doc-content p { color: #a1a1aa; }
                            .doc-content strong { color: white; }
                            .doc-content ul, .doc-content ol { color: #a1a1aa; margin-left: 1.5rem; }
                            .doc-content li::marker { color: #3b82f6; }
                          </style>
                          <div class="doc-content">${selectedItem.content}</div>
                        `)}} />
                      ) : (
                        <pre className="whitespace-pre-wrap font-inter text-sm text-muted-foreground">
                          {selectedItem.content}
                        </pre>
                      )}
                    </div>
                  </ScrollArea>
                )}

                {/* External Link */}
                {selectedItem.item_type === 'link' && selectedItem.url && (
                  <div className="text-center py-12">
                    <LinkIcon className="w-16 h-16 text-primary mx-auto mb-4" />
                    <p className="font-mono text-sm text-muted-foreground mb-4 break-all">
                      {selectedItem.url}
                    </p>
                    <a 
                      href={selectedItem.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                    >
                      <Button className="bg-primary hover:bg-primary/90 rounded-sm">
                        <ExternalLink className="w-4 h-4 mr-2" />
                        Open Link
                      </Button>
                    </a>
                  </div>
                )}
              </CardContent>
            </>
          ) : (
            <CardContent className="p-12 text-center">
              <GraduationCap className="w-16 h-16 text-muted-foreground mx-auto mb-4 opacity-50" />
              <p className="font-chivo font-bold text-xl text-white">Select Training Material</p>
              <p className="font-inter text-sm text-muted-foreground mt-1">
                Choose a video, document, or link from the list
              </p>
            </CardContent>
          )}
        </Card>
      </div>

      {/* Training Viewer Dialog */}
      <Dialog open={viewerOpen} onOpenChange={setViewerOpen}>
        <DialogContent 
          className={`bg-panel border-white/10 rounded-sm ${
            viewerFullscreen 
              ? 'fixed inset-4 max-w-none max-h-none w-auto h-auto' 
              : 'max-w-4xl max-h-[90vh]'
          } overflow-hidden flex flex-col`}
        >
          <DialogHeader className="flex-shrink-0 border-b border-white/10 pb-4">
            <div className="flex items-center justify-between">
              <DialogTitle className="font-chivo font-bold text-xl text-white truncate pr-4">
                {viewerItem?.title || 'Training Viewer'}
              </DialogTitle>
              <div className="flex gap-2 shrink-0">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => downloadItem(viewerItem)}
                  className="border-white/20 rounded-sm"
                  title="Download"
                >
                  <Download className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setViewerFullscreen(!viewerFullscreen)}
                  className="border-white/20 rounded-sm"
                  title={viewerFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
                >
                  {viewerFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
                </Button>
              </div>
            </div>
          </DialogHeader>
          
          <ScrollArea className="flex-1 p-6">
            {viewerItem && (
              <div className="space-y-4">
                {viewerItem.description && (
                  <p className="font-inter text-sm text-muted-foreground">
                    {viewerItem.description}
                  </p>
                )}
                
                {/* Video */}
                {viewerItem.item_type === 'video' && viewerItem.url && (
                  <div className="aspect-video bg-black rounded-sm overflow-hidden">
                    <iframe
                      src={viewerItem.url.replace('watch?v=', 'embed/')}
                      className="w-full h-full"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      title={viewerItem.title}
                    />
                  </div>
                )}
                
                {/* Document */}
                {viewerItem.item_type === 'document' && viewerItem.content && (
                  <div className="prose prose-invert prose-sm max-w-none">
                    {viewerItem.content_type === 'html' ? (
                      <div dangerouslySetInnerHTML={{ 
                        __html: sanitizeHtml(`
                          <style>
                            .doc-content h1 { font-size: 1.5rem; font-weight: 800; color: white; margin: 1.5rem 0 1rem 0; }
                            .doc-content p { color: #a1a1aa; margin: 0.5rem 0; line-height: 1.6; }
                            .doc-content ul, .doc-content ol { margin-left: 1.5rem; color: #a1a1aa; }
                            .doc-content li { margin: 0.25rem 0; }
                          </style>
                          <div class="doc-content">${viewerItem.content}</div>
                        `)
                      }} />
                    ) : (
                      <pre className="font-inter text-sm text-muted-foreground whitespace-pre-wrap">
                        {viewerItem.content}
                      </pre>
                    )}
                  </div>
                )}
                
                {/* Link */}
                {viewerItem.item_type === 'link' && viewerItem.url && (
                  <div className="text-center py-8">
                    <LinkIcon className="w-16 h-16 text-primary mx-auto mb-4" />
                    <p className="font-mono text-sm text-muted-foreground mb-4 break-all">
                      {viewerItem.url}
                    </p>
                    <a 
                      href={viewerItem.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                    >
                      <Button className="bg-primary hover:bg-primary/90 rounded-sm">
                        <ExternalLink className="w-4 h-4 mr-2" />
                        Open Link
                      </Button>
                    </a>
                  </div>
                )}
                
                {/* File (image, PDF, etc.) */}
                {viewerItem.file_url && (
                  <div>
                    {viewerItem.file_url.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
                      <img 
                        src={viewerItem.file_url} 
                        alt={viewerItem.title}
                        className="max-w-full h-auto rounded-sm"
                      />
                    ) : viewerItem.file_url.match(/\.(mp4|webm|mov)$/i) ? (
                      <video 
                        src={viewerItem.file_url} 
                        controls 
                        className="max-w-full h-auto rounded-sm"
                      />
                    ) : viewerItem.file_url.match(/\.pdf$/i) ? (
                      <iframe
                        src={viewerItem.file_url}
                        className="w-full h-[600px] rounded-sm border border-white/10"
                        title={viewerItem.title}
                      />
                    ) : (
                      <div className="bg-surface p-4 rounded-sm text-center">
                        <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-2" />
                        <p className="font-mono text-sm text-muted-foreground">
                          {viewerItem.file_name || 'File'}
                        </p>
                        <Button 
                          onClick={() => downloadItem(viewerItem)}
                          className="mt-4 bg-primary"
                        >
                          <Download className="w-4 h-4 mr-2" />
                          Download File
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Category Item Component
const CategoryItem = ({ 
  category, 
  subcategories, 
  items, 
  allItems,
  expanded, 
  onToggle, 
  onSelectItem, 
  selectedItem,
  onDelete,
  onDeleteItem,
  onViewItem,
  onDownloadItem,
  getItemIcon
}) => {
  const hasContent = items.length > 0 || subcategories.length > 0;
  
  return (
    <div className="mb-2">
      {/* Category Header */}
      <div 
        className="flex items-center gap-2 p-2 rounded-sm hover:bg-white/5 cursor-pointer group"
        onClick={onToggle}
      >
        {hasContent ? (
          expanded ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />
        ) : (
          <div className="w-4" />
        )}
        <span className="font-inter text-sm text-white flex-1">{category.name}</span>
        <Badge variant="secondary" className="font-mono text-[10px]">
          {items.length + subcategories.reduce((acc, sub) => acc + allItems.filter(i => i.category_id === sub.id).length, 0)}
        </Badge>
        {onDelete && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete(category.id);
            }}
            className="opacity-0 group-hover:opacity-100 p-1 hover:bg-alert-red/20 rounded"
          >
            <X className="w-3 h-3 text-alert-red" />
          </button>
        )}
      </div>

      {/* Expanded Content */}
      {expanded && (
        <div className="ml-4 pl-2 border-l border-white/10">
          {/* Subcategories */}
          {subcategories.map(sub => (
            <CategoryItem
              key={sub.id}
              category={sub}
              subcategories={[]}
              items={allItems.filter(i => i.category_id === sub.id)}
              allItems={allItems}
              expanded={true}
              onToggle={() => {}}
              onSelectItem={onSelectItem}
              selectedItem={selectedItem}
              onDelete={onDelete}
              onDeleteItem={onDeleteItem}
              onViewItem={onViewItem}
              onDownloadItem={onDownloadItem}
              getItemIcon={getItemIcon}
            />
          ))}
          
          {/* Items */}
          {items.map(item => {
            const Icon = getItemIcon(item.item_type);
            return (
              <div
                key={item.id}
                className={`flex items-center gap-2 p-2 rounded-sm group ${
                  selectedItem?.id === item.id 
                    ? 'bg-primary/20 border border-primary/50' 
                    : 'hover:bg-white/5'
                }`}
              >
                <div 
                  onClick={() => onSelectItem(item)}
                  className="flex items-center gap-2 flex-1 min-w-0 cursor-pointer"
                >
                  <Icon className="w-4 h-4 text-muted-foreground shrink-0" />
                  <span className="font-inter text-sm text-muted-foreground truncate">
                    {item.title}
                  </span>
                </div>
                {/* View & Download buttons */}
                <div className="flex gap-1 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                  {onViewItem && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onViewItem(item);
                      }}
                      className="p-1 hover:bg-primary/20 rounded"
                      title="View"
                    >
                      <Eye className="w-3 h-3 text-primary" />
                    </button>
                  )}
                  {onDownloadItem && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onDownloadItem(item);
                      }}
                      className="p-1 hover:bg-white/10 rounded"
                      title="Download"
                    >
                      <Download className="w-3 h-3 text-muted-foreground" />
                    </button>
                  )}
                  {onDeleteItem && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onDeleteItem(item.id);
                      }}
                      className="p-1 hover:bg-alert-red/20 rounded"
                    >
                      <X className="w-3 h-3 text-alert-red" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default TrainingPage;
